import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom';
import { useT } from '@/shared/i18n/useT';

interface PasteSplitButtonProps {
  onPaste: () => void;
  onPasteValues: () => void;
  /** The last copy held formulas: the caret offers pasting them or only their values. */
  hasFormulas: boolean;
}

export function PasteSplitButton({ onPaste, onPasteValues, hasFormulas }: PasteSplitButtonProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 8, top: 8 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuLabel = t('grid.pasteMenu');

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const updatePosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 200;
      setMenuPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - menuWidth - 8)),
        top: rect.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!hasFormulas) setOpen(false);
  }, [hasFormulas]);

  const itemClass = 'flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none';

  return (
    <div ref={wrapperRef} className="relative inline-flex h-7 shrink-0 overflow-hidden rounded text-fg">
      <button
        type="button"
        onClick={onPaste}
        title={t('grid.paste')}
        aria-label={t('grid.paste')}
        className={`flex h-7 items-center justify-center transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${hasFormulas ? 'pl-1.5 pr-0.5' : 'w-7'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" />
          <path d="M16 4h2a2 2 0 0 1 2 2v4" />
          <path d="M21 14H11" />
          <path d="m15 10-4 4 4 4" />
        </svg>
      </button>
      {hasFormulas && (
        <button
          type="button"
          aria-label={menuLabel}
          title={menuLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="flex h-7 w-4 items-center justify-start transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <Caret />
        </button>
      )}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={menuLabel}
            style={{ position: 'fixed', left: menuPosition.left, top: menuPosition.top }}
            className="z-50 min-w-48 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-border bg-surface p-1 text-xs shadow-xl flex flex-col gap-0.5"
          >
            <button type="button" role="menuitem" className={itemClass} onClick={() => { setOpen(false); onPaste() }}>
              <span className="text-xs font-normal text-fg">{t('grid.pasteFormulas')}</span>
            </button>
            <button type="button" role="menuitem" className={itemClass} onClick={() => { setOpen(false); onPasteValues() }}>
              <span className="text-xs font-normal text-fg">{t('grid.pasteValues')}</span>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
